import { LEAN_LIBRARY_ENRICHMENT } from '../data/leanLibraryEnrichment';
import { LEAN_LIBRARY_INTELLIGENCE } from '../data/leanLibraryIntelligence';
import type { LeanLibraryItem } from '../types/leanLibrary';
import type { LeanLibraryEnrichment } from '../types/leanLibrary';

export interface EnrichedLibraryView extends LeanLibraryEnrichment {
  isComplete: true;
}

export interface PartialLibraryView {
  isComplete: false;
  swiPassos: string[];
  itensConformidade: string[];
  itensDesvio: string[];
  categoriaOperacional: string;
  indicadoresPrimarios: string[];
  indicadoresSecundarios: string[];
  relacaoCausal: string;
  frequenciaBenchmark: LeanLibraryEnrichment['frequenciaBenchmark'];
  nivelMaturidade: LeanLibraryEnrichment['nivelMaturidade'] | null;
  ielImpacto: LeanLibraryEnrichment['ielImpacto'] | null;
}

export type LeanLibraryConsultantView = EnrichedLibraryView | PartialLibraryView;

export function getLeanLibraryEnrichment(id: string): LeanLibraryEnrichment | undefined {
  const base = LEAN_LIBRARY_ENRICHMENT[id];
  if (!base) return undefined;
  const intel = LEAN_LIBRARY_INTELLIGENCE[id];
  if (!intel) {
    return {
      ...base,
      itensDesvio: [],
      categoriaOperacional: 'Gestão Diária',
    };
  }
  return {
    ...base,
    swiPassos: intel.swiPassos ?? base.swiPassos,
    itensConformidade: intel.itensConformidade ?? base.itensConformidade,
    itensDesvio: intel.itensDesvio,
    categoriaOperacional: intel.categoriaOperacional,
    indicadoresPrimarios: intel.indicadoresPrimarios ?? base.indicadoresPrimarios,
    indicadoresSecundarios: intel.indicadoresSecundarios ?? base.indicadoresSecundarios,
  };
}

function resolveObjetivo(item: LeanLibraryItem): string {
  return LEAN_LIBRARY_INTELLIGENCE[item.id]?.objetivo ?? item.objetivo;
}

function resolveEvidencia(item: LeanLibraryItem): string {
  return LEAN_LIBRARY_INTELLIGENCE[item.id]?.evidenciaEsperada ?? item.evidenciaEsperada;
}

/** Objetivo especialista para importação de rotina da biblioteca. */
export function getLibraryObjetivo(item: LeanLibraryItem): string {
  return resolveObjetivo(item);
}

export function isEnrichedRoutine(id: string): boolean {
  return id in LEAN_LIBRARY_ENRICHMENT;
}

export function getConsultantView(item: LeanLibraryItem): LeanLibraryConsultantView {
  const enrichment = getLeanLibraryEnrichment(item.id);
  if (enrichment) {
    return { ...enrichment, isComplete: true };
  }

  return {
    isComplete: false,
    swiPassos: [
      'Revisar objetivo e evidência esperada da rotina.',
      'Preparar materiais e indicadores de referência.',
      'Executar conforme padrão documentado no posto.',
      'Registrar desvios e abrir plano de ação se necessário.',
      'Comunicar resultados à camada superior quando aplicável.',
    ],
    itensConformidade: [
      'Processo auditado executado conforme padrão',
      'Registros e evidências disponíveis',
      'Desvios identificados e tratados',
    ],
    itensDesvio: [
      'Padrão operacional não atendido',
      'Evidências ausentes ou incompletas',
      'Desvio sem plano de ação',
    ],
    categoriaOperacional: 'Gestão Diária',
    indicadoresPrimarios: ['Aderência à rotina', 'Conformidade do processo'],
    indicadoresSecundarios: ['Tempo de ciclo da rotina', 'Ações abertas'],
    relacaoCausal:
      'Consultor em expansão: em breve esta rotina terá orientação completa. Por ora, aderência e conformidade sustentam disciplina e resultado operacional.',
    frequenciaBenchmark: Object.fromEntries(
      item.nivelHierarquico.map((n) => [n, item.frequenciaRecomendada]),
    ) as LeanLibraryEnrichment['frequenciaBenchmark'],
    nivelMaturidade: null,
    ielImpacto: null,
  };
}

export function buildEnrichedSwiText(item: LeanLibraryItem, view?: LeanLibraryConsultantView): string {
  const consultant = view ?? getConsultantView(item);
  const passos = consultant.swiPassos
    .map((passo, idx) => `${idx + 1}. ${passo}`)
    .join('\n');

  const conforme = consultant.itensConformidade.map((i) => `* ${i}`).join('\n');
  const desvio = consultant.itensDesvio.map((i) => `* ${i}`).join('\n');
  const primarios = consultant.indicadoresPrimarios.map((i) => `* ${i}`).join('\n');
  const secundarios = consultant.indicadoresSecundarios.map((i) => `* ${i}`).join('\n');
  const objetivo = resolveObjetivo(item);
  const evidencia = resolveEvidencia(item);

  return [
    `**Objetivo:** ${objetivo}`,
    '',
    '**Como verificar:**',
    passos,
    '',
    '**Conforme quando:**',
    conforme,
    '',
    '**Desvio quando:**',
    desvio,
    '',
    '**Evidência esperada:**',
    evidencia,
    '',
    '**Indicadores sugeridos — Primários:**',
    primarios,
    '',
    '**Indicadores sugeridos — Secundários:**',
    secundarios,
    '',
    '**Categoria operacional:**',
    consultant.categoriaOperacional,
    '',
    '**Relação causal:**',
    consultant.relacaoCausal,
  ].join('\n');
}

export function buildEnrichedChecklist(
  itens: string[],
  prefix = 'conf',
): Array<{ id: string; text: string }> {
  return itens.map((text, idx) => ({
    id: `${prefix}_${idx + 1}`,
    text,
  }));
}

export function buildEnrichedImportPayload(item: LeanLibraryItem): {
  swi_text: string;
  swi_checklist: Array<{ id: string; text: string }>;
  linked_indicator_name: string | null;
} {
  const view = getConsultantView(item);
  return {
    swi_text: buildEnrichedSwiText(item, view),
    swi_checklist: buildEnrichedChecklist(view.itensConformidade),
    linked_indicator_name: view.indicadoresPrimarios[0] ?? null,
  };
}

export function countEnrichedRoutines(): number {
  return Object.keys(LEAN_LIBRARY_INTELLIGENCE).length;
}
